import { formatTimeDisplay } from './formatters.js';
import { timelineTimeKey, timelineTimeMinutes } from './vendorTimes.js';
const bySortOrder = (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
/** The sections in their order, each with its entries in theirs — a copy, never the cached arrays. */
export function orderedTimelineSections(sections) {
    return [...sections]
        .sort(bySortOrder)
        .map((s) => ({ ...s, entries: [...s.entries].sort(bySortOrder) }));
}
/** An entry's time as it reads on screen — "3:30PM" however it was typed; text that is no time stays as typed. */
export function formatTimelineTime(time) {
    return formatTimeDisplay(timelineTimeKey(time) ?? time ?? '');
}
/** How many entries the sections hold. */
export function timelineEntryCount(sections) {
    return sections.reduce((n, s) => n + s.entries.length, 0);
}
/** The earliest and latest time among the entries (`HH:MM`), or null when none has one. */
export function timelineSpan(entries) {
    let start = null;
    let end = null;
    for (const e of entries) {
        const key = timelineTimeKey(e.time);
        if (!key)
            continue;
        if (start === null || key < start)
            start = key;
        if (end === null || key > end)
            end = key;
    }
    return start && end ? { start, end } : null;
}
/** "5:00PM – 11:30PM", "5:00PM" when it starts and ends together, '' with no times. */
export function formatTimelineSpan(entries) {
    const span = timelineSpan(entries);
    if (!span)
        return '';
    if (span.start === span.end)
        return formatTimeDisplay(span.start);
    return `${formatTimeDisplay(span.start)} – ${formatTimeDisplay(span.end)}`;
}
/**
 * The entries, in the order given, whose time is earlier than a timed entry
 * above them — a cue that will be read out of turn on the day. Entries with
 * no time are never flagged and never move the bar.
 */
export function outOfOrderEntryIds(entries) {
    const out = [];
    let latest = -Infinity;
    for (const e of entries) {
        const minutes = timelineTimeMinutes(e.time);
        if (minutes === Infinity)
            continue;
        if (minutes < latest)
            out.push(e.id);
        else
            latest = minutes;
    }
    return out;
}
/**
 * The entries put in time order. An entry with no time travels with the
 * timed entry above it (a note on that cue); any before the first timed
 * entry stay at the top. Equal times keep their order.
 */
export function entriesInTimeOrder(entries) {
    const lead = [];
    const runs = [];
    for (const e of entries) {
        const minutes = timelineTimeMinutes(e.time);
        if (minutes === Infinity) {
            if (runs.length)
                runs[runs.length - 1].entries.push(e);
            else
                lead.push(e);
        }
        else {
            runs.push({ minutes, index: runs.length, entries: [e] });
        }
    }
    runs.sort((a, b) => a.minutes - b.minutes || a.index - b.index);
    return [...lead, ...runs.flatMap((r) => r.entries)];
}
//# sourceMappingURL=timeline.js.map